import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AdminSessionsNav from './AdminSessionsNav';
import useAdminAutoRefresh from './useAdminAutoRefresh';
import './AdminUsers.css';
import './AdminSessions.css';

const AUTO_REFRESH_INTERVAL_MS = 15000;

function formatDateTime(value) {
  if (!value) {
    return 'Not available';
  }

  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) {
    return 'Not available';
  }

  return date.toLocaleString([], {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function formatDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return 'Not tracked';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${remainingSeconds}s`;
  }

  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }

  return `${remainingSeconds}s`;
}

function formatInteger(value) {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value) || 0);
}

function formatPercentage(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return 'Not available';
  }

  return `${Math.round(Number(value) * 100)}%`;
}

function stringifyStructuredValue(value) {
  if (value === null || value === undefined || value === '') {
    return 'Not available';
  }

  if (typeof value === 'string') {
    return value;
  }

  return JSON.stringify(value, null, 2);
}

function severityClassName(severity) {
  switch (severity) {
    case 'fatal':
      return 'cq-admin-badge cq-admin-badge--error';
    case 'error':
      return 'cq-admin-badge cq-admin-badge--disabled';
    case 'warn':
      return 'cq-admin-badge cq-admin-badge--warn';
    default:
      return 'cq-admin-badge cq-admin-badge--neutral';
  }
}

const AdminSessionDetailPage = () => {
  const { sessionId } = useParams();
  const [showInfo, setShowInfo] = useState(false);
  const [expandedErrorId, setExpandedErrorId] = useState('');
  const [state, setState] = useState({
    loading: true,
    refreshing: false,
    error: '',
    session: null,
    conversationLog: null,
    errors: [],
    lastUpdatedAt: null,
  });
  const { refreshNonce, triggerRefresh } = useAdminAutoRefresh({ intervalMs: AUTO_REFRESH_INTERVAL_MS });

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const controller = new AbortController();

    const loadDetail = async ({ background = false } = {}) => {
      setState((current) => ({
        ...current,
        loading: background ? current.loading : true,
        refreshing: background,
        error: '',
      }));

      try {
        const response = await fetch(`/.netlify/functions/admin-get-session-detail?session_id=${encodeURIComponent(sessionId)}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data?.message || 'Unable to load session details.');
        }

        setState({
          loading: false,
          refreshing: false,
          error: '',
          session: data.session || null,
          conversationLog: data.conversation_log || null,
          errors: data.errors || [],
          lastUpdatedAt: new Date().toISOString(),
        });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        setState({
          loading: false,
          refreshing: false,
          error: error.message || 'Unable to load session details.',
          session: null,
          conversationLog: null,
          errors: [],
          lastUpdatedAt: null,
        });
      }
    };

    loadDetail();

    return () => {
      controller.abort();
    };
  }, [refreshNonce, sessionId]);

  const visibleErrors = useMemo(
    () => state.errors.filter((item) => showInfo || item.severity !== 'info'),
    [showInfo, state.errors],
  );

  return (
    <section className="cq-admin-page">
      <header className="cq-admin-page__header">
        <div>
          <p className="cq-admin-page__eyebrow">Session Monitoring</p>
          <h2 className="cq-admin-page__title">Session Detail</h2>
          <p className="cq-admin-page__copy">Inspect session context, completion state, and related errors in one place.</p>
        </div>
        <div className="cq-admin-page__actions">
          <div className="cq-admin-page__actions-row">
            <Link className="cq-admin-status-link" to="/admin/sessions">Back to Sessions</Link>
            <button
              type="button"
              className="cq-admin-action-button cq-admin-action-button--secondary"
              disabled={state.loading || state.refreshing}
              onClick={triggerRefresh}
            >
              Refresh
            </button>
          </div>
          <p className="cq-admin-page__meta">
            {state.refreshing ? 'Refreshing...' : `Last updated ${formatDateTime(state.lastUpdatedAt)}`}
          </p>
        </div>
      </header>

      <AdminSessionsNav />

      <div className="cq-admin-session-crumbs">
        <span className="cq-admin-cell-id">{sessionId}</span>
      </div>

      {state.loading ? <div className="cq-admin-empty-state"><p className="cq-admin-empty-state__title">Loading session details...</p></div> : null}
      {!state.loading && state.error ? <div className="cq-admin-empty-state"><p className="cq-admin-empty-state__title">{state.error}</p></div> : null}

      {!state.loading && !state.error && state.session ? (
        <>
          <div className="cq-admin-users-grid">
            <section className="cq-admin-panel">
              <h3 className="cq-admin-section-title">Session Overview</h3>
              <dl className="cq-admin-definition-list">
                <div><dt>Session ID</dt><dd className="cq-admin-cell-id">{state.session.id}</dd></div>
                <div><dt>Game Pack</dt><dd>{state.session.game_pack?.name || 'Unknown'}</dd></div>
                <div><dt>Game Mode</dt><dd>{state.session.game_mode?.name || 'Unknown'}</dd></div>
                <div><dt>Host</dt><dd>{state.session.host?.name || 'Unknown'}</dd></div>
                <div><dt>Quest Pack</dt><dd>{state.session.quest_pack?.name || 'Not attached'}</dd></div>
                <div><dt>Status</dt><dd>{state.session.status}</dd></div>
                <div><dt>Players</dt><dd>{state.session.player_count ?? 'Not tracked'}</dd></div>
                <div><dt>Start Time</dt><dd>{formatDateTime(state.session.started_at || state.session.created_at)}</dd></div>
                <div><dt>End Time</dt><dd>{formatDateTime(state.session.ended_at)}</dd></div>
                <div><dt>Duration</dt><dd>{formatDuration(state.session.duration_seconds)}</dd></div>
                <div><dt>Ended Reason</dt><dd>{state.session.ended_reason || 'Not available'}</dd></div>
              </dl>
            </section>

            <section className="cq-admin-panel">
              <h3 className="cq-admin-section-title">Conversation Detail</h3>
              <dl className="cq-admin-definition-list">
                <div><dt>Conversation Log</dt><dd className="cq-admin-cell-id">{state.conversationLog?.id || 'Not available'}</dd></div>
                <div><dt>Status</dt><dd>{state.conversationLog?.status || 'Not available'}</dd></div>
                <div><dt>Model</dt><dd>{state.conversationLog?.model_used || 'Not available'}</dd></div>
                <div><dt>Reasoning Effort</dt><dd>{state.conversationLog?.reasoning_effort || state.session?.reasoning_effort || 'Not available'}</dd></div>
                <div><dt>Environment</dt><dd>{state.conversationLog?.environment || 'Not available'}</dd></div>
                <div><dt>Started At</dt><dd>{formatDateTime(state.conversationLog?.started_at)}</dd></div>
                <div><dt>Ended At</dt><dd>{formatDateTime(state.conversationLog?.ended_at)}</dd></div>
                <div><dt>Total Cost</dt><dd>{state.conversationLog ? `$${Number(state.conversationLog.total_cost || 0).toFixed(4)}` : 'Not available'}</dd></div>
                <div><dt>Message Count</dt><dd>{state.session.message_count || state.conversationLog?.message_count || 0}</dd></div>
                <div><dt>AI Reply Count</dt><dd>{state.session.ai_reply_count || 0}</dd></div>
                <div><dt>Billing Status</dt><dd>{state.session.billing_status || 'not_charged'}</dd></div>
                <div><dt>Credits Charged</dt><dd>{state.session.credits_charged || 0}</dd></div>
                <div><dt>Refunded Credits</dt><dd>{state.session.refunded_credits || 0}</dd></div>
                <div><dt>Startup Error</dt><dd>{state.session.startup_error || 'None'}</dd></div>
                <div><dt>Max Input Tokens</dt><dd>{state.conversationLog?.max_input_tokens ? formatInteger(state.conversationLog.max_input_tokens) : 'Not available'}</dd></div>
                <div><dt>Max Output Tokens</dt><dd>{state.conversationLog?.max_output_tokens ? formatInteger(state.conversationLog.max_output_tokens) : 'Not available'}</dd></div>
                <div><dt>Warning Threshold</dt><dd>{formatPercentage(state.conversationLog?.warning_threshold)}</dd></div>
                <div><dt>Input Tokens</dt><dd>{formatInteger(state.conversationLog?.new_input_tokens)}</dd></div>
                <div><dt>Cached Tokens</dt><dd>{formatInteger(state.conversationLog?.cached_tokens)}</dd></div>
                <div><dt>Output Tokens</dt><dd>{formatInteger(state.conversationLog?.output_tokens)}</dd></div>
                <div><dt>Input Text Tokens</dt><dd>{formatInteger(state.conversationLog?.new_input_text_tokens)}</dd></div>
                <div><dt>Input Audio Tokens</dt><dd>{formatInteger(state.conversationLog?.new_input_audio_tokens)}</dd></div>
                <div><dt>Cached Text Tokens</dt><dd>{formatInteger(state.conversationLog?.cached_text_tokens)}</dd></div>
                <div><dt>Cached Audio Tokens</dt><dd>{formatInteger(state.conversationLog?.cached_audio_tokens)}</dd></div>
                <div><dt>Output Text Tokens</dt><dd>{formatInteger(state.conversationLog?.output_text_tokens)}</dd></div>
                <div><dt>Output Audio Tokens</dt><dd>{formatInteger(state.conversationLog?.output_audio_tokens)}</dd></div>
              </dl>
            </section>
          </div>

          {state.conversationLog ? (
            <section className="cq-admin-panel">
              <h3 className="cq-admin-section-title">Conversation Metadata</h3>
              <div className="cq-admin-users-grid">
                <div>
                  <h4 className="cq-admin-session-subtitle">Error Details</h4>
                  <pre className="cq-admin-pre">{stringifyStructuredValue(state.conversationLog.error_details)}</pre>
                </div>
                <div>
                  <h4 className="cq-admin-session-subtitle">Client Info</h4>
                  <pre className="cq-admin-pre">{stringifyStructuredValue(state.conversationLog.client_info)}</pre>
                </div>
              </div>
              <div className="cq-admin-session-error-block">
                <h4 className="cq-admin-session-subtitle">Usage Events</h4>
                <pre className="cq-admin-pre">{stringifyStructuredValue(state.conversationLog.usage_events)}</pre>
              </div>
            </section>
          ) : null}

          <section className="cq-admin-panel">
            <div className="cq-admin-summary-card__header">
              <div>
                <h3>Errors</h3>
                <p className="cq-admin-users-count">{visibleErrors.length} visible error event{visibleErrors.length === 1 ? '' : 's'}</p>
              </div>
              <label className="cq-admin-session-toggle">
                <input type="checkbox" checked={showInfo} onChange={(event) => setShowInfo(event.target.checked)} />
                <span>Show info events</span>
              </label>
            </div>

            {!visibleErrors.length ? (
              <div className="cq-admin-empty-inline">No errors found for this session.</div>
            ) : (
              <div className="cq-admin-table-wrap">
                <table className="cq-admin-table">
                  <thead>
                    <tr>
                      <th>Occurred At</th>
                      <th>Severity</th>
                      <th>Layer</th>
                      <th>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleErrors.map((error) => {
                      const expanded = expandedErrorId === error.id;

                      return (
                        <React.Fragment key={error.id}>
                          <tr className="cq-admin-session-row-clickable" onClick={() => setExpandedErrorId(expanded ? '' : error.id)}>
                            <td>{formatDateTime(error.occurred_at)}</td>
                            <td><span className={severityClassName(error.severity)}>{error.severity}</span></td>
                            <td>{error.layer}</td>
                            <td>{error.message}</td>
                          </tr>
                          {expanded ? (
                            <tr>
                              <td colSpan={4}>
                                <div className="cq-admin-session-error-detail">
                                  <dl className="cq-admin-definition-list">
                                    <div><dt>Category</dt><dd>{error.category || 'Not available'}</dd></div>
                                    <div><dt>Function Name</dt><dd>{error.function_name || 'Not available'}</dd></div>
                                    <div><dt>Route</dt><dd>{error.route || 'Not available'}</dd></div>
                                    <div><dt>Provider</dt><dd>{error.provider || 'Not available'}</dd></div>
                                    <div><dt>Request ID</dt><dd>{error.request_id || 'Not available'}</dd></div>
                                    <div><dt>HTTP Status</dt><dd>{error.http_status || 'Not available'}</dd></div>
                                    <div><dt>Conversation ID</dt><dd className="cq-admin-cell-id">{error.conversation_id || 'Not available'}</dd></div>
                                    <div><dt>User ID</dt><dd className="cq-admin-cell-id">{error.user_id || 'Not available'}</dd></div>
                                  </dl>
                                  <div className="cq-admin-session-error-block">
                                    <h4>Full Message</h4>
                                    <pre className="cq-admin-pre">{error.message || 'Not available'}</pre>
                                  </div>
                                  <div className="cq-admin-session-error-block">
                                    <h4>Stack Trace</h4>
                                    <pre className="cq-admin-pre">{error.stack || 'Not available'}</pre>
                                  </div>
                                  <div className="cq-admin-session-error-block">
                                    <h4>Metadata</h4>
                                    <pre className="cq-admin-pre">{JSON.stringify(error.metadata || {}, null, 2)}</pre>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : null}
    </section>
  );
};

export default AdminSessionDetailPage;
